import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '../../lib/supabase/client';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Receipt, Plus, Edit, DollarSign, Calendar, Trash2, Download } from 'lucide-react';
import { generateInvoicePDF } from '../lib/pdfGenerator';
import '../components/styles.css';

export default function Invoices() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [formData, setFormData] = useState({
    invoice_number: '',
    client_id: '',
    project_id: '',
    quote_id: '',
    title: '',
    description: '',
    status: 'draft',
    total_amount: '',
    tax_rate: '20.00',
    paid_amount: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    payment_method: '',
    payment_reference: '',
    notes: ''
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const supabase = createClient();
        const { data: { user: currentUser } } = await supabase.auth.getUser();

        if (!currentUser) {
          navigate('/crm/login');
          return;
        }

        setUser(currentUser);
      } catch (error) {
        console.error('Erreur d\'authentification:', error);
        navigate('/crm/login');
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchInvoices();
      fetchClients();
      fetchProjects();
      fetchQuotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filterStatus]);

  const fetchClients = async () => {
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('clients')
        .select('id, last_name, company_name, client_type')
        .eq('status', 'actif')
        .order('last_name', { ascending: true });
      setClients(data || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('projects')
        .select('id, name')
        .order('name', { ascending: true });
      setProjects(data || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('quotes')
        .select('id, quote_number, title')
        .eq('status', 'accepted')
        .order('quote_number', { ascending: false });
      setQuotes(data || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const supabase = createClient();
      
      // Récupérer les factures
      let query = supabase
        .from('invoices')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data: invoicesData, error: invoicesError } = await query;

      if (invoicesError) {
        throw invoicesError;
      }

      // Récupérer les clients associés
      const clientIds = [...new Set((invoicesData || []).map(i => i.client_id).filter(Boolean))];
      let clientsData = {};
      
      if (clientIds.length > 0) {
        const { data: clients, error: clientsError } = await supabase
          .from('clients')
          .select('id, last_name, company_name, client_type')
          .in('id', clientIds);

        if (!clientsError && clients) {
          clients.forEach(client => {
            clientsData[client.id] = client;
          });
        }
      }

      // Récupérer les projets associés
      const projectIds = [...new Set((invoicesData || []).map(i => i.project_id).filter(Boolean))];
      let projectsData = {};
      
      if (projectIds.length > 0) {
        const { data: projects, error: projectsError } = await supabase
          .from('projects')
          .select('id, name')
          .in('id', projectIds);

        if (!projectsError && projects) {
          projects.forEach(project => {
            projectsData[project.id] = project;
          });
        }
      }

      // Récupérer les devis associés
      const quoteIds = [...new Set((invoicesData || []).map(i => i.quote_id).filter(Boolean))];
      let quotesData = {};
      
      if (quoteIds.length > 0) {
        const { data: quotes, error: quotesError } = await supabase
          .from('quotes')
          .select('id, quote_number')
          .in('id', quoteIds);

        if (!quotesError && quotes) {
          quotes.forEach(quote => {
            quotesData[quote.id] = quote;
          });
        }
      }

      // Joindre les données
      const invoicesWithRelations = (invoicesData || []).map(invoice => ({
        ...invoice,
        clients: invoice.client_id ? clientsData[invoice.client_id] : null,
        projects: invoice.project_id ? projectsData[invoice.project_id] : null,
        quotes: invoice.quote_id ? quotesData[invoice.quote_id] : null
      }));

      setInvoices(invoicesWithRelations);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la récupération des factures: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const generateInvoiceNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `FAC-${year}${month}-${random}`;
  };

  const calculateTotals = (amount, taxRate) => {
    const total = parseFloat(amount) || 0;
    const rate = parseFloat(taxRate) || 0;
    const tax = (total * rate) / 100;
    const totalWithTax = total + tax;
    return { tax, totalWithTax };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const supabase = createClient();

    const { tax, totalWithTax } = calculateTotals(formData.total_amount, formData.tax_rate);

    const invoiceData = {
      ...formData,
      invoice_number: formData.invoice_number || generateInvoiceNumber(),
      total_amount: parseFloat(formData.total_amount) || 0,
      tax_rate: parseFloat(formData.tax_rate) || 20,
      tax_amount: tax,
      total_with_tax: totalWithTax,
      paid_amount: parseFloat(formData.paid_amount) || 0,
      client_id: formData.client_id || null,
      project_id: formData.project_id || null,
      quote_id: formData.quote_id || null,
      issue_date: formData.issue_date || new Date().toISOString().split('T')[0],
      due_date: formData.due_date || null
    };

    if (editingInvoice) {
      const { error } = await supabase
        .from('invoices')
        .update(invoiceData)
        .eq('id', editingInvoice.id);

      if (error) {
        console.error('Erreur lors de la mise à jour:', error);
        alert('Erreur lors de la mise à jour de la facture');
      } else {
        setShowForm(false);
        setEditingInvoice(null);
        resetForm();
        fetchInvoices();
      }
    } else {
      const { error } = await supabase
        .from('invoices')
        .insert([invoiceData]);

      if (error) {
        console.error('Erreur lors de la création:', error);
        alert('Erreur lors de la création de la facture');
      } else {
        setShowForm(false);
        resetForm();
        fetchInvoices();
      }
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      invoice_number: invoice.invoice_number || '',
      client_id: invoice.client_id || '',
      project_id: invoice.project_id || '',
      quote_id: invoice.quote_id || '',
      title: invoice.title || '',
      description: invoice.description || '',
      status: invoice.status || 'draft',
      total_amount: invoice.total_amount || '',
      tax_rate: invoice.tax_rate ? String(invoice.tax_rate) : '20.00',
      paid_amount: invoice.paid_amount || '',
      issue_date: invoice.issue_date || new Date().toISOString().split('T')[0],
      due_date: invoice.due_date || '',
      payment_method: invoice.payment_method || '',
      payment_reference: invoice.payment_reference || '',
      notes: invoice.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ? Cette action est irréversible.')) {
      return;
    }

    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression de la facture');
      } else {
        fetchInvoices();
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression de la facture');
    }
  };

  const resetForm = () => {
    setFormData({
      invoice_number: '',
      client_id: '',
      project_id: '',
      quote_id: '',
      title: '',
      description: '',
      status: 'draft',
      total_amount: '',
      tax_rate: '20.00',
      paid_amount: '',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
      payment_method: '',
      payment_reference: '',
      notes: ''
    });
    setEditingInvoice(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft': return 'Brouillon';
      case 'sent': return 'Envoyée';
      case 'paid': return 'Payée';
      case 'overdue': return 'En retard';
      case 'cancelled': return 'Annulée';
      default: return status;
    }
  };

  const getClientName = (invoice) => {
    if (invoice.clients) {
      return invoice.clients.client_type === 'entreprise' 
        ? invoice.clients.company_name 
        : invoice.clients.last_name;
    }
    return 'Non assigné';
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      await generateInvoicePDF(invoice, invoice.clients, invoice.projects);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center py-8">Chargement des factures...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Factures</h1>
            <p className="mt-2 text-gray-600">Gérez vos factures et paiements</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setFormData({ ...formData, invoice_number: generateInvoiceNumber() });
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Nouvelle facture
          </button>
        </div>

        <div className="flex space-x-4 mb-6">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="all">Toutes les factures</option>
            <option value="draft">Brouillon</option>
            <option value="sent">Envoyée</option>
            <option value="paid">Payée</option>
            <option value="overdue">En retard</option>
            <option value="cancelled">Annulée</option>
          </select>
        </div>

        {showForm && (
          <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">
              {editingInvoice ? 'Modifier la facture' : 'Nouvelle facture'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro de facture *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut *
                  </label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="draft">Brouillon</option>
                    <option value="sent">Envoyée</option>
                    <option value="paid">Payée</option>
                    <option value="overdue">En retard</option>
                    <option value="cancelled">Annulée</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client *
                  </label>
                  <select
                    required
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Sélectionner un client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.client_type === 'entreprise' ? client.company_name : client.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chantier
                  </label>
                  <select
                    value={formData.project_id}
                    onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Aucun chantier</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Devis
                  </label>
                  <select
                    value={formData.quote_id}
                    onChange={(e) => setFormData({ ...formData, quote_id: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Aucun devis</option>
                    {quotes.map((quote) => (
                      <option key={quote.id} value={quote.id}>
                        {quote.quote_number} - {quote.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titre *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Montant HT (€) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.total_amount}
                    onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Taux de TVA (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Montant payé (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.paid_amount}
                    onChange={(e) => setFormData({ ...formData, paid_amount: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date d'émission *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date d'échéance
                  </label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mode de paiement
                  </label>
                  <input
                    type="text"
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    className="w-full p-2 border rounded-md"
                    placeholder="Ex: Virement, Chèque, Espèces"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Référence de paiement
                  </label>
                  <input
                    type="text"
                    value={formData.payment_reference}
                    onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
                    className="w-full p-2 border rounded-md"
                    placeholder="Ex: VIR-2024-001"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  className="w-full p-2 border rounded-md"
                />
              </div>

              {formData.total_amount && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-sm text-gray-600">
                    Montant HT: {parseFloat(formData.total_amount || 0).toFixed(2)}€
                  </p>
                  <p className="text-sm text-gray-600">
                    TVA ({formData.tax_rate}%): {calculateTotals(formData.total_amount, formData.tax_rate).tax.toFixed(2)}€
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    Montant TTC: {calculateTotals(formData.total_amount, formData.tax_rate).totalWithTax.toFixed(2)}€
                  </p>
                  {formData.paid_amount && (
                    <p className="text-sm text-gray-600 mt-2">
                      Payé: {parseFloat(formData.paid_amount).toFixed(2)}€
                      {' / '}
                      Restant: {(calculateTotals(formData.total_amount, formData.tax_rate).totalWithTax - parseFloat(formData.paid_amount || 0)).toFixed(2)}€
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingInvoice ? 'Mettre à jour' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {invoices.length === 0 ? (
              <li className="p-6 text-center text-gray-500">Aucune facture trouvée.</li>
            ) : (
              invoices.map((invoice) => (
                <li key={invoice.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Receipt className="h-5 w-5 text-primary-600" />
                        <span className="text-lg font-semibold text-gray-900">
                          {invoice.invoice_number || 'Sans numéro'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                          {getStatusLabel(invoice.status)}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p className="font-medium text-gray-900">{invoice.title}</p>
                        <p>Client: <span className="font-medium">{getClientName(invoice)}</span></p>
                        {invoice.projects && (
                          <p>Chantier: <span className="font-medium">{invoice.projects.name}</span></p>
                        )}
                        {invoice.description && <p>{invoice.description}</p>}
                        <p className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          <span className="font-semibold text-primary-600">
                            {invoice.total_with_tax?.toFixed(2) || '0.00'}€ TTC
                          </span>
                          {invoice.total_amount && (
                            <span className="text-gray-500">
                              ({invoice.total_amount.toFixed(2)}€ HT)
                            </span>
                          )}
                          {invoice.paid_amount > 0 && (
                            <span className="text-green-600">
                              • Payé: {invoice.paid_amount.toFixed(2)}€
                            </span>
                          )}
                        </p>
                        {invoice.issue_date && (
                          <p className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Émise le {new Date(invoice.issue_date).toLocaleDateString('fr-FR')}
                            {invoice.due_date && (
                              <> • Échéance: {new Date(invoice.due_date).toLocaleDateString('fr-FR')}</>
                            )}
                          </p>
                        )}
                        {invoice.payment_method && (
                          <p>Paiement: {invoice.payment_method} {invoice.payment_reference && `(${invoice.payment_reference})`}</p>
                        )}
                        {invoice.notes && (
                          <p className="text-gray-700 mt-2">{invoice.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end space-y-2 ml-4">
                      <span className="text-xs text-gray-500">
                        {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDownloadPDF(invoice)}
                          className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-primary-700 bg-primary-100 rounded-md hover:bg-primary-200"
                          title="Télécharger en PDF"
                        >
                          <Download className="h-4 w-4" />
                          PDF
                        </button>
                        <button
                          onClick={() => handleEdit(invoice)}
                          className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          <Edit className="h-4 w-4" />
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(invoice.id)}
                          className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200"
                        >
                          <Trash2 className="h-4 w-4" />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}
